import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AllTokenVaultActions,
  TokenVaultFilterKey,
  TokenVaultStatuses,
  TokenVaultVerification,
  ZoudashUtils,
} from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import {
  TokenVaultActionDisplayPipe,
  TokenVaultStatusDisplayPipe,
  TokenVaultVerificationDisplayPipe,
} from '@workspace/ng-data-access';
import { TokenVaultColumnKey } from '../abstract-token-vaults-data-table.component';

export interface TokenVaultFilterModel {
  action: string | string[] | null;
  status: string | string[] | null;
  verification: string | string[] | null;
}

@Component({
  selector: 'core-token-vaults-data-table-filter',
  templateUrl: './token-vaults-data-table-filter.component.html',
  imports: [
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSlideToggleModule,
    TokenVaultActionDisplayPipe,
    TokenVaultStatusDisplayPipe,
    TokenVaultVerificationDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TokenVaultsDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<TokenVaultFilterKey, string | string[]>>,
  TokenVaultFilterModel
> {
  filterChanged = output<FilterChangedDetection>();
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<TokenVaultColumnKey>[]>();

  tokenVaultActionValues = Object.values(AllTokenVaultActions);
  tokenVaultStatusValues = Object.values(TokenVaultStatuses);
  tokenVaultVerificationValues = Object.values(TokenVaultVerification);
  tokenVaultFilterKey = TokenVaultFilterKey;

  override model: WritableSignal<TokenVaultFilterModel> = signal<TokenVaultFilterModel>({
    action: null,
    status: null,
    verification: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<TokenVaultFilterKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<TokenVaultFilterKey, string | string[]>>,
  ): TokenVaultFilterModel {
    return {
      action: dto?.[TokenVaultFilterKey.Action] ?? null,
      status: dto?.[TokenVaultFilterKey.Status] ?? null,
      verification: dto?.[TokenVaultFilterKey.Verification] ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: TokenVaultFilterModel,
  ): Partial<Record<TokenVaultFilterKey, string | string[]>> {
    const dto: Partial<Record<TokenVaultFilterKey, string | string[]>> = {
      [TokenVaultFilterKey.Action]: model.action ?? undefined,
      [TokenVaultFilterKey.Status]: model.status ?? undefined,
      [TokenVaultFilterKey.Verification]: model.verification ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([_, v]) => v != null)) as Partial<
      Record<TokenVaultFilterKey, string | string[]>
    >;
  }
}
